import { useState, type FormEvent } from "react";

import { api, readError } from "../api/client";
import { setActiveClinic, startSession, type Assignment } from "../api/session";
import { CodeInput } from "../components/CodeInput";
import { ErrorNote } from "../components/ErrorNote";
import { QrCode } from "../components/QrCode";

/**
 * Sign in: password, then the authenticator code, then the clinic.
 *
 * Three steps, one screen, plus an enrolment step the first time. The first
 * two are IDN-F-020: a password alone never reaches a chart. The last is
 * ADR-008, because a portal session is a person *at a clinic* and a session
 * with no clinic chosen can call nothing under /portal.
 */
type Step =
  | { name: "password" }
  | { name: "enrol"; totpToken: string; uri: string; secret: string }
  | { name: "code"; totpToken: string }
  | { name: "clinic"; assignments: Assignment[] };

export function SignIn() {
  const [step, setStep] = useState<Step>({ name: "password" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /* The six boxes are a view of this, not six pieces of state that can
     disagree with each other. */
  const [code, setCode] = useState("");
  /* Reveal, not store. The board draws the eye and nothing in brief 01
     section 10 forbids it; typing a long password blind on a reception
     desk is how people end up writing it on the monitor. */
  const [showPassword, setShowPassword] = useState(false);

  async function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);

    const { data, error: failed } = await api.POST("/auth/staff/login", {
      body: {
        email: String(form.get("email") ?? ""),
        password: String(form.get("password") ?? ""),
      },
    });

    if (failed || !data?.totp_token) {
      setBusy(false);
      setError(readError(failed).message);
      return;
    }

    if (data.totp_enrolment_required) {
      await startEnrolment(data.totp_token);
      return;
    }
    setBusy(false);
    setStep({ name: "code", totpToken: data.totp_token });
  }

  /** A request with no code asks for the provisioning URI (IDN-F-020). */
  async function startEnrolment(totpToken: string) {
    const { data, error: failed } = await api.POST("/auth/staff/totp", {
      body: { totp_token: totpToken },
    });
    setBusy(false);

    if (failed || !data || !("provisioning_uri" in data)) {
      setError(readError(failed).message);
      return;
    }
    setStep({
      name: "enrol",
      totpToken,
      uri: data.provisioning_uri,
      secret: data.secret,
    });
  }

  async function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step.name !== "code" && step.name !== "enrol") return;
    setBusy(true);
    setError(null);

    const { data, error: failed } = await api.POST("/auth/staff/totp", {
      body: { totp_token: step.totpToken, code },
    });
    setBusy(false);

    if (failed || !data || !("access_token" in data)) {
      setError(readError(failed).message);
      return;
    }

    startSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      full_name: data.full_name,
    });
    const assignments = (data.assignments ?? []) as Assignment[];

    // One assignment is the common case, and asking about it wastes a click.
    if (assignments.length === 1) {
      chooseClinic(assignments[0]!);
      return;
    }
    setStep({ name: "clinic", assignments });
  }

  function chooseClinic(assignment: Assignment) {
    setActiveClinic({
      id: assignment.clinic_id,
      name: assignment.clinic_name,
      role: assignment.role,
    });
  }

  function restart() {
    setError(null);
    setCode("");
    setStep({ name: "password" });
  }

  const codeField = (
    <>
      <CodeInput value={code} onChange={setCode} autoFocus invalid={!!error} />
      <ErrorNote message={error} />
      {/* Refused until there are six of them. An incomplete code is not a
          wrong code, and sending it would answer a question nobody asked
          with the error meant for somebody who mistyped. */}
      <button type="submit" disabled={busy || code.length < 6}>
        {busy ? "Checking…" : "Sign in"}
      </button>
      <button type="button" className="link" onClick={restart}>
        Start again
      </button>
    </>
  );

  return (
    <div className="signin">
      {/* The photograph is the whole left half, as the approved mockup has it,
          and it carries one line of its own. It is decoration with a job: the
          portal is the first thing a clinic sees of Healtrics, and a bare form
          on a white page says nothing about what they have bought. */}
      <aside className="signin-art" aria-hidden="true">
        <p className="signin-art-words">
          Secure access
          <br />
          for better care
        </p>
      </aside>

      <div className="signin-panel">
        <div className="card">
          <p className="signin-wordmark">Healtrics</p>

        {step.name === "password" && (
          <form onSubmit={submitPassword}>
            <h1 className="signin-title">Welcome</h1>
            <p className="lead">Sign in to the clinic portal.</p>

            <label htmlFor="email">Work email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              placeholder="name@yourclinic.com"
              required
            />

            <label htmlFor="password">Password</label>
            <div className="field-with-action">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="field-action"
                onClick={() => setShowPassword((on) => !on)}
                aria-pressed={showPassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <ErrorNote message={error} />
            <button type="submit" className="wide" disabled={busy}>
              {busy ? "Checking…" : "Continue →"}
            </button>
          </form>
        )}

        {step.name === "enrol" && (
          <form onSubmit={submitCode}>
            <p className="lead">
              Set up your authenticator app. Scan this once, then enter the code it shows.
            </p>

            <QrCode value={step.uri} />

            <p className="muted">
              If you cannot scan, type this key into the app instead:
              <br />
              <code className="secret">{step.secret}</code>
            </p>

            {codeField}
          </form>
        )}

        {step.name === "code" && (
          <form onSubmit={submitCode}>
            <p className="lead">Enter the six-digit code from your authenticator app.</p>
            {codeField}
          </form>
        )}

        {step.name === "clinic" && (
          <div>
            <p className="lead">Choose the clinic you are working at.</p>
            {step.assignments.length === 0 ? (
              <p className="empty">
                This account has no active clinic assignment. A clinic administrator adds one.
              </p>
            ) : (
              <ul className="clinics">
                {step.assignments.map((assignment) => (
                  <li key={assignment.clinic_id}>
                    <button type="button" onClick={() => chooseClinic(assignment)}>
                      <span className="clinic">{assignment.clinic_name}</span>
                      <span className="role">{assignment.role.replace("_", " ")}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
